import { useEffect, useRef, useState } from "react";
import { ChevronDown, Check, Search, X } from "lucide-react";
import api from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 25;

// Server-backed selector: search + load-more over a paginated endpoint, never a client-side cap.
// fetchPage(q, page) must resolve to { items, total, page, page_size }.
export function AsyncPaginatedSelect({ testId, value, onChange, fetchPage, getLabel = (i) => i.name, getSub, placeholder = "Select…", disabled, selectedLabel, emptyText = "No matches", className, clearable = true }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [active, setActive] = useState(-1);
  const [label, setLabel] = useState(selectedLabel || "");
  const [selectedItem, setSelectedItem] = useState(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const reqRef = useRef(0);

  useEffect(() => { if (selectedLabel) setLabel(selectedLabel); }, [selectedLabel]);
  useEffect(() => { if (!value) { setLabel(""); setSelectedItem(null); } }, [value]);

  const load = async (query, nextPage, append) => {
    const ticket = ++reqRef.current;
    setLoading(true); setFailed(false);
    try {
      const d = await fetchPage(query, nextPage);
      if (ticket !== reqRef.current) return;
      setItems((prev) => (append ? [...prev, ...d.items] : d.items));
      setTotal(d.total); setPage(d.page);
      if (!append) setActive(d.items.length ? 0 : -1);
    } catch { if (ticket === reqRef.current) setFailed(true); } finally { if (ticket === reqRef.current) setLoading(false); }
  };

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => load(q.trim() || undefined, 1, false), q ? 250 : 0);
    return () => clearTimeout(t);
  }, [q, open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 30); else setQ(""); }, [open]);

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${active}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const hasMore = items.length < total;
  // the current selection stays visible (pinned first) even when it is not on the loaded page
  const pinned = selectedItem && value && !q && !items.some((i) => i.id === value) ? [selectedItem] : [];
  const rows = [...pinned, ...items];
  const choose = (item) => { setLabel(getLabel(item)); setSelectedItem(item); onChange(item.id, item); setOpen(false); };
  const clear = (e) => { e.stopPropagation(); setLabel(""); setSelectedItem(null); onChange("", null); };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, rows.length - 1 + (hasMore ? 1 : 0))); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (active >= 0 && active < rows.length) choose(rows[active]);
      else if (active === rows.length && hasMore && !loading) load(q.trim() || undefined, page + 1, true);
    } else if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
  };

  const listId = `${testId}-listbox`;
  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <button type="button" role="combobox" aria-expanded={open} aria-controls={listId} aria-haspopup="listbox" disabled={disabled} data-testid={testId}
          className={cn("flex h-10 w-full items-center justify-between rounded-md border border-jigsaw-border bg-white px-3 py-2 text-left text-sm text-jigsaw-navy shadow-sm focus:outline-none focus:ring-2 focus:ring-jigsaw-orange/40 disabled:cursor-not-allowed disabled:opacity-50", className)}>
          <span data-testid={`${testId}-value`} className={cn("truncate", !label && "text-jigsaw-muted")}>{label || placeholder}</span>
          <span className="ml-2 flex shrink-0 items-center gap-1 text-jigsaw-muted">
            {value && clearable && !disabled && <X data-testid={`${testId}-clear`} aria-label="Clear selection" role="button" tabIndex={-1} className="h-3.5 w-3.5 hover:text-jigsaw-navy" onClick={clear} />}
            <ChevronDown className="h-4 w-4 opacity-60" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0" onOpenAutoFocus={(e) => e.preventDefault()}>
        <div className="flex items-center gap-2 border-b border-jigsaw-border px-3 py-2">
          <Search className="h-3.5 w-3.5 shrink-0 text-jigsaw-muted" />
          <input ref={inputRef} data-testid={`${testId}-search`} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} placeholder="Type to search…"
            role="searchbox" aria-controls={listId} aria-activedescendant={active >= 0 && active < rows.length ? `${listId}-opt-${active}` : undefined}
            className="h-7 w-full bg-transparent text-sm text-jigsaw-navy outline-none placeholder:text-jigsaw-muted" />
        </div>
        <div ref={listRef} id={listId} role="listbox" data-testid={listId} className="max-h-64 overflow-y-auto py-1">
          {failed && <p data-testid={`${testId}-error`} className="px-3 py-2 text-sm text-jigsaw-danger">Could not load options.</p>}
          {!failed && !loading && rows.length === 0 && <p data-testid={`${testId}-empty`} className="px-3 py-2 text-sm text-jigsaw-muted">{emptyText}</p>}
          {rows.map((item, i) => {
            const selected = item.id === value;
            return (
              <div key={item.id} id={`${listId}-opt-${i}`} data-index={i} role="option" aria-selected={selected} data-testid={`${testId}-option-${item.id}`}
                onMouseEnter={() => setActive(i)} onMouseDown={(e) => e.preventDefault()} onClick={() => choose(item)}
                className={cn("flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm", i === active ? "bg-jigsaw-bg" : "", selected && "font-medium text-jigsaw-navy")}>
                <span className="min-w-0">
                  <span className="block truncate">{getLabel(item)}</span>
                  {getSub && <span className="block truncate text-[12px] text-jigsaw-muted">{getSub(item)}</span>}
                </span>
                {selected && <Check className="h-3.5 w-3.5 shrink-0 text-jigsaw-orange" />}
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-jigsaw-border px-3 py-1.5 text-[12px] text-jigsaw-muted">
          <span data-testid={`${testId}-count`} role="status">{loading ? "Loading…" : total ? `Showing ${items.length} of ${total}` : ""}</span>
          {hasMore && (
            <button type="button" data-testid={`${testId}-load-more`} data-index={rows.length} disabled={loading} onMouseDown={(e) => e.preventDefault()}
              onClick={() => load(q.trim() || undefined, page + 1, true)}
              className={cn("rounded px-2 py-0.5 font-medium text-jigsaw-navy hover:bg-jigsaw-bg disabled:opacity-50", active === rows.length && "bg-jigsaw-bg ring-1 ring-jigsaw-orange/40")}>
              Load {Math.min(PAGE_SIZE, total - items.length)} more
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export const directoryFetcher = (path, extra = {}) => async (q, page) => {
  const r = await api.get(path, { params: { q, page, page_size: PAGE_SIZE, ...extra } });
  return r.data;
};
